import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, AlertTriangle, ChevronDown, ChevronUp, X } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { formatDateLabel } from '@/lib/format';
import { useHolidays } from '@/hooks/useHolidays';
import { addDays, buildDateList } from '@/lib/calendar-day';
import { type NullReason, NULL_REASON_LABELS } from '@/lib/shift-report-labels';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { DateLabel } from '@/components/DateLabel';
import { Modal } from '@/components/Modal';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { ViolationDetail } from '@/components/ViolationDetail';
import { personKey, isoToLocalHm } from '@/lib/shift-format';

/**
 * `/shift/periods/:id/coordination` (v0.4 章④/⑤ F-2、D-48。章④ (D-350) で日別過不足マトリクスを追加)。
 * 全社最終確定の前に、余剰店と不足店を突き合わせ応援枠を付け替える画面 (§5-15 含意3 の双方向プロセス)。
 * 設計: docs/design/v04-shift-labor-management-detail.md §4-10 / v04-cross-employee-shift-report-detail.md §5
 *
 * ⚠️ `GET /shift/periods/:id/coordination` は `shift_violations` (staffing_shortage/excess) の
 * 集約であり対象者名を持たない (店舗×日付の集約ビューのため)。応援付替えの対象となる個別の割当は
 * `GET /shift/assignments?...` から都度取得し、氏名は `GET /standing-patterns?node_id=` の
 * ロースターから解決する (ShiftBoardPage と同じ縮退方針: ロースター取得に失敗しても機能停止しない)。
 *
 * D-350/D-360: 日別マトリクス (`daily_matrix`) は既定で折りたたみ、`?expand_matrix=1` で明示的に
 * 展開したときだけサーバー側で計算される (659店舗規模テナントでの無条件計算を避ける)。
 */

type CoordinationEntry = { violation_id: string; node_id: string; node_name: string; work_date: string | null;
  detail: Record<string, unknown> | null;
  /** どの必要人員要件 (staffing_constraint_groups.label) 由来かを示す。
   *  ⚠️ 無いと「武蔵小杉店 / 9月16日(水) ・ 必要2 ・ 実績0」が要件ごとに複数行並び区別できない
   *  (2026-09-03 staging 実機検証)。特に「終日」由来の恒久的な不足を他と見分けるのに要る。 */
  group_label: string | null };
type DailyMatrixCell = {
  node_id: string; work_date: string;
  headcount_diff: number | null;
  violation_counts: { hard: number; soft: number };
  null_reasons: Partial<Record<string, NullReason>>;
};
type DailyMatrix = {
  range: { from: string; to: string };
  nodes: Array<{ node_id: string; node_name: string }>;
  cells: DailyMatrixCell[];
};
type CoordinationResponse = {
  period: { period_id: string; status: string };
  shortfalls: CoordinationEntry[]; surpluses: CoordinationEntry[];
  daily_matrix: DailyMatrix | null;
};

type AssignmentRow = {
  assignment_id: string; employee_id: string | null; external_person_id: string | null;
  work_date: string; start_at: string; end_at: string; work_node_id: string; row_version: number; origin: string;
};
type RosterPerson = { employee_id: string | null; external_person_id: string | null; display_name: string };

function ReassignModal({
  periodId, sourceNode, shortfallNodes, onClose, onDone,
}: {
  periodId: string; sourceNode: CoordinationEntry; shortfallNodes: { node_id: string; node_name: string }[];
  onClose: () => void; onDone: () => void;
}) {
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string>('');
  const [destinationNodeId, setDestinationNodeId] = useState<string>('');

  const { data: assignmentsData, isLoading } = useQuery<{ assignments: AssignmentRow[] }>({
    queryKey: ['shift-assignments-by-node-date', periodId, sourceNode.node_id, sourceNode.work_date],
    queryFn: () => apiFetch(
      `/shift/assignments?period_id=${periodId}&node_id=${sourceNode.node_id}`
      + (sourceNode.work_date ? `&work_date_from=${sourceNode.work_date}&work_date_to=${sourceNode.work_date}` : ''),
    ),
  });
  const assignments = assignmentsData?.assignments ?? [];

  const { data: rosterData } = useQuery<{ people: RosterPerson[] }>({
    queryKey: ['shift-roster-for-reassign', sourceNode.node_id],
    queryFn: () => apiFetch(`/standing-patterns?node_id=${sourceNode.node_id}`),
    retry: false,
  });
  const nameByKey = useMemo(() => new Map((rosterData?.people ?? []).map((p) => [personKey(p), p.display_name])), [rosterData]);

  const reassignMut = useMutation({
    mutationFn: () => {
      const target = assignments.find((a) => a.assignment_id === selectedAssignmentId);
      if (!target) throw new Error('割当を選択してください');
      return apiFetch(`/shift/periods/${periodId}/coordination/reassign`, {
        method: 'POST',
        body: JSON.stringify({ assignment_id: target.assignment_id, row_version: target.row_version, new_work_node_id: destinationNodeId }),
      });
    },
    onSuccess: () => { toast.success('応援先へ付け替えました'); onDone(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '付替えに失敗しました'),
  });

  const destinations = shortfallNodes.filter((n) => n.node_id !== sourceNode.node_id);

  return (
    <Modal
      open title={`${sourceNode.node_name}のシフトを応援に回す`} onClose={onClose} size="sm"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button
            variant="primary" disabled={!selectedAssignmentId || !destinationNodeId} loading={reassignMut.isPending}
            onClick={() => reassignMut.mutate()}
          >
            付け替える
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-text-sub mb-1">付け替える枠</label>
          {isLoading ? (
            <div className="text-sm text-text-sub">読み込み中…</div>
          ) : assignments.length === 0 ? (
            <p className="text-sm text-text-sub">この店舗・日付に割当がありません。</p>
          ) : (
            <div className="space-y-1">
              {assignments.map((a) => (
                <label key={a.assignment_id} className="flex items-center gap-2 text-sm px-2 py-2 border border-surface-border rounded min-h-[44px]">
                  <input
                    type="radio" name="assignment" value={a.assignment_id}
                    checked={selectedAssignmentId === a.assignment_id}
                    onChange={() => setSelectedAssignmentId(a.assignment_id)}
                  />
                  <span>{nameByKey.get(personKey(a)) ?? '(氏名不明)'}</span>
                  <span className="text-text-sub tabular-nums">{formatDateLabel(a.work_date)} {isoToLocalHm(a.start_at)}〜{isoToLocalHm(a.end_at)}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">応援先店舗</label>
          {destinations.length === 0 ? (
            <p className="text-sm text-text-sub">不足店がありません。</p>
          ) : (
            <Select value={destinationNodeId || undefined} onValueChange={setDestinationNodeId}>
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="店舗を選ぶ" /></SelectTrigger>
              <SelectContent>
                {destinations.map((n) => <SelectItem key={n.node_id} value={n.node_id}>{n.node_name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
    </Modal>
  );
}

function EntryRow({
  e, kind, onPickForReassign, holidays,
}: {
  e: CoordinationEntry; kind: 'shortfall' | 'surplus'; onPickForReassign?: (e: CoordinationEntry) => void;
  holidays: ReadonlyMap<string, string>;
}) {
  return (
    <div className="p-2.5 border border-surface-border rounded-lg text-sm flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="font-semibold text-text-body flex items-center gap-1.5">
          <AlertTriangle size={13} aria-hidden className="text-alert-text shrink-0" />
          {e.node_name}
        </div>
        <div className="text-xs text-text-sub mt-0.5">
          {e.work_date && <span><DateLabel date={e.work_date} withWeekday holidays={holidays} /> ・ </span>}
          {e.group_label && <span className="break-words">{e.group_label} ・ </span>}
          <ViolationDetail detail={e.detail} />
        </div>
      </div>
      {kind === 'surplus' && onPickForReassign && (
        <Button variant="secondary" size="sm" onClick={() => onPickForReassign(e)}>応援に回す</Button>
      )}
    </div>
  );
}

function EntryList({
  entries, kind, onPickForReassign, holidays,
}: {
  entries: CoordinationEntry[]; kind: 'shortfall' | 'surplus'; onPickForReassign?: (e: CoordinationEntry) => void;
  holidays: ReadonlyMap<string, string>;
}) {
  if (entries.length === 0) {
    return <p className="text-sm text-text-sub">{kind === 'shortfall' ? '不足している店舗はありません。' : '余剰の店舗はありません。'}</p>;
  }
  return (
    <div className="space-y-2">
      {entries.map((e) => (
        <EntryRow key={e.violation_id} e={e} kind={kind} onPickForReassign={onPickForReassign} holidays={holidays} />
      ))}
    </div>
  );
}

/**
 * v0.4 追加要件⑪ 章④ (D-350): 日別×店舗別の過不足マトリクス。既定で折りたたみ、展開したときだけ
 * `?expand_matrix=1` 付きで取得する (D-360、バックエンドの `assertWithinReportRangeLimits` で
 * 上限超過時は REPORT_RANGE_TOO_LARGE を返す)。**日付列見出し**のクリックで `onCellClick` を通じて
 * 下部の突き合わせリストを該当日にフィルタする (再クリックで解除、親側でトグルする)。
 *
 * ⚠️ design-expertレビュー指摘 (CRITICAL) 反映: 当初はセル本体 (最大62列×店舗数) をボタンにしていたが、
 * 44px タップ領域を全セルに適用すると密な表全体が実用性を失う。日付は列単位で意味が決まる (どの店舗の
 * セルをクリックしても同じ日付に絞り込むだけ) ため、クリック可能な領域を「1行だけの日付列見出し」に
 * 集約する形に変更した (44px要件を1行だけ満たせばよく、セル本体は非インタラクティブな表示に戻せる)。
 * ⚠️ 同レビュー指摘: 過不足マイナス値は `text-brand-red` (CTA/アクセント用) ではなく、下部の
 * 不足店リストのアイコン (`text-alert-text`) と揃える (ui-guidelines §2.2「赤は1画面1箇所」)。
 */
function DailyMatrixPanel({
  periodId, expanded, onToggleExpanded, filterDate, onCellClick,
}: {
  periodId: string; expanded: boolean; onToggleExpanded: () => void;
  filterDate: string | null; onCellClick: (date: string) => void;
}) {
  const { data, isLoading, isError, error } = useQuery<{ daily_matrix: DailyMatrix | null }>({
    queryKey: ['shift-coordination-matrix', periodId],
    queryFn: () => apiFetch(`/shift/periods/${periodId}/coordination?expand_matrix=1`),
    enabled: expanded && !!periodId,
  });
  const matrix = data?.daily_matrix ?? null;
  // 半開区間 [range.from, range.to) を、既存の buildDateList (inclusive) に合わせて to-1日 で列挙する
  const dates = useMemo(() => (matrix ? buildDateList(matrix.range.from, addDays(matrix.range.to, -1)) : []), [matrix]);
  const holidays = useHolidays(matrix?.range.from ?? '', matrix?.range.to ?? '');
  const cellByKey = useMemo(() => {
    const map = new Map<string, DailyMatrixCell>();
    for (const cell of matrix?.cells ?? []) map.set(`${cell.node_id}|${cell.work_date}`, cell);
    return map;
  }, [matrix]);
  const rangeTooLarge = isError && error instanceof ApiClientError && error.code === 'REPORT_RANGE_TOO_LARGE';
  // design-and-code/platform-reviewer 指摘 (consensus): report_metric_catalog は shift_report
  // フラグ必須のため、フラグOFFのテナントでは (エラーではなく) daily_matrix:null が返る。
  // ノードは有るのに中身が無い状態を「対象店舗がありません」と混同して表示しない。
  const noDataAfterLoad = !isLoading && !isError && expanded && data !== undefined && matrix === null;

  return (
    <div className="border border-surface-border rounded-lg bg-white p-3">
      <button
        type="button" onClick={onToggleExpanded} aria-expanded={expanded}
        className="flex items-center gap-1.5 min-h-[44px] text-sm font-semibold text-text-body"
      >
        {expanded ? <ChevronUp size={16} aria-hidden /> : <ChevronDown size={16} aria-hidden />}
        日別過不足マトリクス
      </button>
      {!expanded && <p className="text-xs text-text-sub">この期間全体の店舗×日別の過不足を表示します (クリックで展開)。</p>}
      {expanded && (
        <div className="mt-2">
          {isLoading && <div className="py-4 text-sm text-text-sub" aria-live="polite">読み込み中…</div>}
          {isError && (
            <div className="py-2 text-sm text-danger-text">
              {rangeTooLarge ? '対象範囲が大きすぎるため表示できません。店舗数や期間を絞ってください。' : 'マトリクスの取得に失敗しました。'}
              {error instanceof ApiClientError && error.message && <span className="block mt-0.5 text-xs text-text-sub">{error.message}</span>}
            </div>
          )}
          {noDataAfterLoad && <p className="py-2 text-sm text-text-sub">この機能は現在利用できません。</p>}
          {matrix && matrix.nodes.length === 0 && <p className="py-2 text-sm text-text-sub">対象店舗がありません。</p>}
          {matrix && matrix.nodes.length > 0 && (
            <>
              {filterDate && (
                <button
                  type="button" onClick={() => onCellClick(filterDate)}
                  className="mb-2 inline-flex items-center gap-1 min-h-[44px] px-3 rounded-full bg-surface-sunken text-xs text-text-body"
                >
                  <DateLabel date={filterDate} withWeekday holidays={holidays} />に絞り込み中
                  <X size={12} aria-hidden />
                </button>
              )}
              <p className="mb-1 text-[11px] text-text-sub">→ 表は横にスクロールできます</p>
              <div className="overflow-x-auto">
                <table className="border-collapse w-full text-sm">
                  <thead>
                    <tr>
                      <th scope="col" className="sticky left-0 z-10 bg-surface-sunken border-b border-r border-surface-border px-3 py-2 text-left text-xs text-text-sub" style={{ minWidth: 120 }}>
                        店舗
                      </th>
                      {dates.map((date) => {
                        const isActive = filterDate === date;
                        return (
                          <th key={date} scope="col" className="border-b border-surface-border p-0 text-center whitespace-nowrap" style={{ minWidth: 56 }}>
                            <button
                              type="button" onClick={() => onCellClick(date)} aria-pressed={isActive}
                              aria-label={`${date}の実績・予定に絞り込む`}
                              className={`w-full min-h-[44px] px-2 text-xs hover:bg-surface-sunken ${isActive ? 'bg-misespark-navy/10 font-semibold' : ''}`}
                            >
                              <DateLabel date={date} withWeekday holidays={holidays} />
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {matrix.nodes.map((node) => (
                      <tr key={node.node_id} className="odd:bg-white even:bg-surface-stripe">
                        <th
                          scope="row" title={node.node_name}
                          className="sticky left-0 z-10 bg-inherit border-r border-surface-border px-3 py-1.5 max-w-[140px] truncate text-left text-xs font-normal text-text-body"
                        >
                          {node.node_name}
                        </th>
                        {dates.map((date) => {
                          const cell = cellByKey.get(`${node.node_id}|${date}`);
                          const diff = cell?.headcount_diff ?? null;
                          const reason = cell?.null_reasons.headcount_diff;
                          const isActive = filterDate === date;
                          return (
                            <td
                              key={date}
                              className={`border-b border-surface-border px-2 py-1.5 text-center text-xs tabular-nums whitespace-nowrap ${isActive ? 'bg-misespark-navy/10' : ''} ${diff !== null && diff < 0 ? 'text-alert-text font-medium' : 'text-text-body'}`}
                            >
                              {diff !== null ? (diff > 0 ? `+${diff}` : diff) : (reason ? NULL_REASON_LABELS[reason] : '—')}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function ShiftCoordinationPage() {
  const { id: periodId = '' } = useParams();
  const qc = useQueryClient();
  const [reassignTarget, setReassignTarget] = useState<CoordinationEntry | null>(null);
  const [matrixExpanded, setMatrixExpanded] = useState(false);
  const [matrixFilterDate, setMatrixFilterDate] = useState<string | null>(null);
  const toggleMatrixFilterDate = (date: string) => setMatrixFilterDate((cur) => (cur === date ? null : date));

  const { data, isLoading, isError, refetch } = useQuery<CoordinationResponse>({
    queryKey: ['shift-coordination', periodId],
    queryFn: () => apiFetch(`/shift/periods/${periodId}/coordination`),
    enabled: !!periodId,
  });

  const shortfalls = useMemo(
    () => (matrixFilterDate ? (data?.shortfalls ?? []).filter((e) => e.work_date === matrixFilterDate) : data?.shortfalls ?? []),
    [data, matrixFilterDate],
  );
  const surpluses = useMemo(
    () => (matrixFilterDate ? (data?.surpluses ?? []).filter((e) => e.work_date === matrixFilterDate) : data?.surpluses ?? []),
    [data, matrixFilterDate],
  );

  const shortfallNodes = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of data?.shortfalls ?? []) map.set(e.node_id, e.node_name);
    return [...map.entries()].map(([node_id, node_name]) => ({ node_id, node_name }));
  }, [data]);

  // ⚠️ QAレビュー指摘反映: 行 (EntryRow) ごとに node_id 付きで useHolidays を呼ぶと店舗数だけ
  // リクエストが分岐するN+1になるため、テナント既定 (node_id なし) のみを1回まとめて取得し
  // 全行で共有する (店舗固有休日は §6d の判断で対象外、渡来武規模では非問題)。
  const allWorkDates = useMemo(
    () => [...(data?.shortfalls ?? []), ...(data?.surpluses ?? [])]
      .map((e) => e.work_date).filter((d): d is string => !!d),
    [data],
  );
  const minDate = allWorkDates.length > 0 ? allWorkDates.reduce((a, b) => (a < b ? a : b)) : '';
  const maxDate = allWorkDates.length > 0 ? allWorkDates.reduce((a, b) => (a > b ? a : b)) : '';
  const holidays = useHolidays(minDate, maxDate);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['shift-coordination', periodId] });
    qc.invalidateQueries({ queryKey: ['shift-board'] });
  };

  if (!periodId) return null;

  return (
    <div className="space-y-4">
      <div>
        <Link to={`/shift/periods/${periodId}/board`} className="inline-flex items-center gap-1 min-h-[44px] text-xs text-misespark-navy hover:underline">
          <ArrowLeft size={14} aria-hidden />編成ボードに戻る
        </Link>
        <h2 className="text-h2 mt-1">全社調整</h2>
        <p className="text-sm text-text-sub mt-1">余剰店のシフトを不足店へ応援として付け替えられます。</p>
      </div>

      <DailyMatrixPanel
        periodId={periodId} expanded={matrixExpanded} onToggleExpanded={() => setMatrixExpanded((v) => !v)}
        filterDate={matrixFilterDate} onCellClick={toggleMatrixFilterDate}
      />

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : isError ? (
        <div className="p-6 border border-danger-border bg-danger-bg rounded-lg text-danger-text flex flex-wrap items-center justify-between gap-2">
          <span>全社調整の取得に失敗しました。全店舗スコープの権限が必要です。権限をご確認のうえ、もう一度お試しください。</span>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>再試行</Button>
        </div>
      ) : !data || (data.shortfalls.length === 0 && data.surpluses.length === 0) ? (
        <EmptyState message="調整が必要な店舗はありません" description="人員不足・過剰の警告が出ている店舗があると、ここに表示されます。" />
      ) : matrixFilterDate && shortfalls.length === 0 && surpluses.length === 0 ? (
        <EmptyState
          message="この日の不足・余剰はありません" description="別の日付を選ぶか、絞り込みを解除してください。"
          actionLabel="絞り込みを解除" onAction={() => setMatrixFilterDate(null)}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="border border-surface-border rounded-lg bg-white p-3">
            <h3 className="text-sm font-semibold text-text-body mb-2">不足店</h3>
            <EntryList entries={shortfalls} kind="shortfall" holidays={holidays} />
          </div>
          <div className="border border-surface-border rounded-lg bg-white p-3">
            <h3 className="text-sm font-semibold text-text-body mb-2 flex items-center gap-1">
              余剰店<ArrowRight size={12} aria-hidden className="text-text-sub" />不足店
            </h3>
            <EntryList entries={surpluses} kind="surplus" onPickForReassign={setReassignTarget} holidays={holidays} />
          </div>
        </div>
      )}

      {reassignTarget && (
        <ReassignModal
          periodId={periodId} sourceNode={reassignTarget} shortfallNodes={shortfallNodes}
          onClose={() => setReassignTarget(null)}
          onDone={() => { setReassignTarget(null); invalidate(); }}
        />
      )}
    </div>
  );
}
